import React, { useState, useEffect } from 'react'
import { BrowserRouter, Route, Redirect } from 'react-router-dom'
import Header from './Header/Header'
import Login from './Login/Login'
import ForgotPassword from './Login/ForgotPassword'
import NewPassword from './Login/NewPassword'
import jwtDecode from 'jwt-decode'
import AppContainer from './UI/AppContainer'
import * as Sentry from '@sentry/browser'
import { api } from './Controllers/api'
import { logoutUser, checkLogin, loginUser } from './Store/actions'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { getScreenSize } from './UI/Store/actions'
import ErrorBoundary from './ErrorBoundary/ErrorBoundary'

const { NODE_ENV, REACT_APP_SENTRY_KEY } = process.env

// enable sentry in production only
if (NODE_ENV === 'production') {
  // sentry error tracking
  Sentry.init({ dsn: REACT_APP_SENTRY_KEY })
}

const App = props => {
  const [loading, setLoading] = useState(true)
  const [loginError, setLoginError] = useState()
  const [isLoggingIn, setIsLoggingIn] = useState(false)

  const setUserProperties = data => {
    localStorage.setItem('auth', data)

    const user = jwtDecode(data)
    props.loginUser(user)
    if (NODE_ENV === 'production') {
      Sentry.configureScope(scope => {
        scope.setUser({ username: `${user.firstName} ${user.lastName}`, email: user.email })
      })
    }
    // for safari we need to set the cookie
    document.cookie = `access=${data}; path=/; expires=${new Date().getTime() +
      1000 * 60 * 60 * 48}; HttpOnly;`
  }

  const login = (username, password, formRef, successCallback) => {
    const body = { username, password }
    setIsLoggingIn(true)
    api
      .post('weblogin', body)
      .then(response => {
        if (response) {
          localStorage.setItem('userToken', username)
          const { data } = response
          setUserProperties(data)
          setIsLoggingIn(false)
          successCallback()
          setLoading(false)
          setLoginError('')
        } else {
          console.log('no user')
          setLoading(false)
        }
      })
      .catch(error => {
        formRef.current.setSubmitting(false)
        console.log(error)
        setLoading(false)
        setIsLoggingIn(false)
        setLoginError('Password incorrect')
      })
  }

  const loginSms = (phoneNumber, formRef, successCallback) => {
    const body = { phoneNumber }
    setIsLoggingIn(true)
    api
      .post('weblogin/smslogin', body)
      .then(response => {
        if (response && response.data === 'Phone number confirmed') {
          successCallback()
          setLoading(false)
          setLoginError('')
          setIsLoggingIn(false)
        } else {
          console.log('no user')
          setLoading(false)
        }
      })
      .catch(error => {
        formRef.current.setSubmitting(false)
        console.log(error)
        setIsLoggingIn(false)
        setLoading(false)
        setLoginError('Password incorrect')
      })
  }

  const loginSmsVerify = (phoneNumber, smsCode, formRef, successCallback) => {
    const body = { phoneNumber, smsCode }
    setIsLoggingIn(true)
    api
      .post('weblogin/smsverify', body)
      .then(response => {
        if (response) {
          localStorage.setItem('userToken', phoneNumber)
          const { data } = response
          setUserProperties(data)
          successCallback()
          setLoading(false)
          setLoginError('')
          setIsLoggingIn(false)
        } else {
          console.log('no user')
          setLoading(false)
          setIsLoggingIn(false)
        }
      })
      .catch(error => {
        formRef.current.setSubmitting(false)
        console.log(error)
        setLoading(false)
        setIsLoggingIn(false)
        setLoginError('Password incorrect')
      })
  }

  const logout = () => {
    props.logoutUser()
  }

  const PrivateRoute = ({ authed, component: Component, logout, ...rest }) => {
    return (
      <Route
        {...rest}
        render={props =>
          authed ? <Component {...props} logout={logout} /> : <Redirect to="/login" />
        }
      />
    )
  }

  useEffect(() => {
    if (!props.loggedIn) {
      props.checkLogin(() => setLoading(false))
    } else {
      setLoading(false)
    }
  }, [props.loggedIn, loading]) //eslint-disable-line

  useEffect(() => {
    props.getScreenSize()
  }, []) //eslint-disable-line

  useEffect(() => {
    if (props.hasLoggedOut) {
      setLoading(false)
    }
  }, [props.hasLoggedOut]) //eslint-disable-line

  if (props.loading || loading) {
    return <div></div>
  }
  return (
    <ErrorBoundary>
      <AppContainer>
        <BrowserRouter>
          <Route exact path="/" render={() => <Redirect to="/app/hauls/" />} />
          <Route exact path="/app" render={() => <Redirect to="/app/hauls/" />} />
          <Route
            path="/login"
            render={props => (
              <Login
                error={loginError}
                login={login}
                {...props}
                loginSms={loginSms}
                loginSmsVerify={loginSmsVerify}
                isLoggingIn={isLoggingIn}
              />
            )}
          />
          <Route path="/forgotpassword" render={props => <ForgotPassword {...props} />} />
          <Route path="/newpassword" render={props => <NewPassword {...props} />} />
          <PrivateRoute authed={props.loggedIn} path="/app" component={Header} logout={logout} />
        </BrowserRouter>
      </AppContainer>
    </ErrorBoundary>
  )
}

const mapStateToProps = state => {
  return {
    loggedIn: state.user.loggedIn,
    loading: state.user.loading,
    hasLoggedOut: state.user.hasLoggedOut
  }
}

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      logoutUser,
      checkLogin,
      loginUser,
      getScreenSize
    },
    dispatch
  )

export default connect(mapStateToProps, mapDispatchToProps)(App)
